/**
 * Creative satellite (text fields + the Phase 12 text ranking) ↔ draft
 * mapping. Defensive like the gateway parse: malformed textGroupIds degrade
 * to [], a malformed ranking to its defaults — never a crash.
 * dropDefaultsJson stays the Options tab's business.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  type DropGenCreativeDetail,
  type DropGenTextRankingConfig,
  defaultTextRanking,
  parseTextRanking,
  withoutHitTexts,
} from '@trafficpulse/shared';

/** The creative satellite's text fields (Options owns dropDefaultsJson). */
export interface DraftCreative {
  textGroupIds: string[];
  assignment: string;
  textMode: string;
  amount: number;
  translate: boolean;
  /** Phase 12 — how texts are ranked and how much the ranking reuses. */
  ranking: DropGenTextRankingConfig;
}

export const DEFAULT_CREATIVE: DraftCreative = {
  textGroupIds: [],
  assignment: 'ROTATE',
  textMode: 'UNIQUE_PER_DROP',
  amount: 1,
  translate: false,
  ranking: defaultTextRanking(),
};

export function creativeToDraft(c: DropGenCreativeDetail | null): DraftCreative {
  if (!c) return { ...DEFAULT_CREATIVE, textGroupIds: [], ranking: defaultTextRanking() };
  return {
    textGroupIds: parseGroupIds(c.textGroupIds),
    assignment: c.assignment,
    textMode: c.textMode,
    amount: c.amount,
    translate: c.translate,
    ranking: textsRanking(c.textRankingJson),
  };
}

/** The Texts ranking without a hit-again line (21/10/2026 — it lives on Hit again, the verdict row). */
function textsRanking(json: unknown): DropGenTextRankingConfig {
  const ranking = parseTextRanking(json);
  return { ...ranking, policy: withoutHitTexts(ranking.policy) };
}

export function parseGroupIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === 'string');
}
